import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import clsx from "clsx";

import { Paper, makeStyles, Box, Typography, Button, IconButton } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { teal } from "@material-ui/core/colors";
import PersonIcon from "@material-ui/icons/Person";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import LinkOffIcon from "@material-ui/icons/LinkOff";

import ContactDrawer from "../ContactDrawer";
import MessageSearchDrawer from "../MessageSearchDrawer";
import MessageInput from "../MessageInputCustom/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import useContactWhatsappStatus from "../../hooks/useContactWhatsappStatus";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import CloudApiWindowBanner from "../CloudApiWindowBanner";
import ConnectionDisconnectedBanner from "../ConnectionDisconnectedBanner";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import hasOpenOverlay from "../../helpers/hasOpenOverlay";
import { isDarkTheme } from "../../helpers/darkTint";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import { isAdminUser, isInactiveTicket } from "../../helpers/canAccessTicket";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  otherUserWarning: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    backgroundColor: isDarkTheme(theme) ? theme.palette.background.paper : "#f5f5f5",
    borderTop: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#e0e0e0"}`,
  },

  warningText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: isDarkTheme(theme) ? theme.palette.text.secondary : "#666",
    fontSize: "14px",
  },

  assumeButton: {
    backgroundColor: "#2196f3",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1976d2",
    },
  },

  // O header é um flex container sem `alignItems`, então o padrão `stretch`
  // esticava o botão na altura toda do cabeçalho: com `border-radius: 50%` o
  // resultado virava uma elipse. `alignSelf` devolve o tamanho natural (quadrado)
  // e `flex: none` impede que ele encolha na largura quando o header lota.
  backButton: {
    marginLeft: 8,
    flex: "none",
    alignSelf: "center",
  },

  chatDropZone: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },

  chatDropOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 10,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed #00aeef",
    backgroundColor: "rgba(0, 174, 239, 0.12)",
    color: "#00aeef",
    fontSize: 18,
    fontWeight: 700,
  },
}));

const Ticket = ({
  ticketIdProp,
  isModal = false,
  onCloseModal,
  jumpToMessageIdProp = null,
  jumpToMessageTsProp = null,
  messageHistoryModeOverride = null
}) => {
  const { ticketId: ticketIdParam } = useParams();
  const ticketId = ticketIdProp || ticketIdParam;
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);

  const { user } = useContext(AuthContext);
  const userQueueIdsKey = useMemo(
    () => (Array.isArray(user?.queues) ? user.queues.map((q) => q.id).sort((a, b) => a - b).join(",") : ""),
    [user?.queues]
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [agentName, setAgentName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [aiTypingAgent, setAiTypingAgent] = useState(null);
  const aiTypingTimerRef = useRef(null);
  const [cloudApiWindowOpen, setCloudApiWindowOpen] = useState(true);
  const [drawerJump, setDrawerJump] = useState({ id: null, ts: null, ticketId: null });
  const ticketIdForSocket = ticket?.id; // Extrair ticket.id para usar como dependência estável
  // O componente é reaproveitado entre tickets (sem key): ao trocar de rota, o
  // `ticket` do estado ainda é o anterior até o GET do novo voltar. Nessa janela
  // nada do ticket velho pode ficar montado — o MessagesList dele continuaria
  // pedindo páginas/imagens e receberia o salto destinado ao ticket novo
  // ("mensagem não encontrada").
  const ticketMatchesRoute =
    Boolean(ticket?.id) &&
    (String(ticket.uuid) === String(ticketId) || String(ticket.id) === String(ticketId));
  const isSwitchingTicket = Boolean(ticket?.id) && !ticketMatchesRoute;
  const jumpToMessageId = jumpToMessageIdProp || location?.state?.jumpToMessageId || null;
  const jumpToMessageTs = jumpToMessageTsProp || location?.state?.jumpToMessageTs || null;
  const shouldUseDrawerJump =
    drawerJump?.ts &&
    (!jumpToMessageTs || drawerJump.ts >= jumpToMessageTs) &&
    Number(drawerJump.ticketId) === Number(ticket?.id);

  const effectiveJumpToMessageId = shouldUseDrawerJump ? drawerJump.id : jumpToMessageId;
  const effectiveJumpToMessageTs = shouldUseDrawerJump ? drawerJump.ts : jumpToMessageTs;

  const socketManager = useContext(SocketContext);
  // Presença, recado e status de 24h do contato (só conexão QR).
  const whatsappStatus = useContactWhatsappStatus(
    ticketMatchesRoute ? ticketIdForSocket : null,
    contact?.id
  );

  // Guardado em ref porque quem monta o popup passa `onCloseModal` como arrow
  // inline (KanbanCard, Conferência): nas deps do efeito de busca ele mudaria de
  // identidade a cada render do pai e refaria o GET em loop.
  const onCloseModalRef = useRef(onCloseModal);
  useEffect(() => {
    onCloseModalRef.current = onCloseModal;
  });

  // Lidos pelo handler de `connectionInactivated` sem entrar nas deps do socket.
  const ticketWhatsappIdRef = useRef(null);
  const isAdminRef = useRef(false);
  useEffect(() => {
    ticketWhatsappIdRef.current = ticket?.whatsappId ? Number(ticket.whatsappId) : null;
    isAdminRef.current = isAdminUser(user);
  });

  useEffect(() => {
    // Valida se ticketId existe antes de fazer a requisição
    if (!ticketId) {
      console.warn('[Ticket] ticketId não definido, abortando requisição');
      setLoading(false);
      return;
    }

    // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: evitar recarregamento visual completo
    // quando apenas referências do contexto mudam (ex.: após envio/socket) mantendo o ticket já exibido.
    // PERF: só mostra a tela de "loading" se a busca demorar >180ms — evita o flash
    // da bolinha em respostas rápidas.
    let loadingTimer = null;
    if (!ticket?.id || isSwitchingTicket) {
      loadingTimer = setTimeout(() => setLoading(true), 180);
    }
    const abortController = new AbortController();
    const fetchTicket = async () => {
      try {
        const { data } = await api.get("/tickets/u/" + ticketId, { signal: abortController.signal });
        // O contato do ticket pode ter sido removido por merge de duplicados
        // (ticket órfão); sem este stub o render quebra e derruba a página.
        if (!data.contact) {
          data.contact = {
            id: data.contactId,
            name: i18n.t("tickets.contactRemoved"),
            number: "",
            profilePicUrl: null,
            extraInfo: [],
          };
        }
        // A permissão é decidida pelo backend (403 ERR_NO_PERMISSION). A checagem
        // que existia aqui só olhava fila + allTicket e divergia da lista, barrando
        // tickets sem fila de conexões que o usuário gerencia.
        if (loadingTimer) clearTimeout(loadingTimer);
        setContact(data.contact);
        setTicket(data);
        setLoading(false);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        if (loadingTimer) clearTimeout(loadingTimer);
        setLoading(false);
        toastError(err);
        if (err?.response?.status === 403) {
          // No popup navegar seria pior que não fazer nada: derrubaria a tela de
          // origem (Fluxos, Pendentes, Conferência...) junto com o próprio popup.
          // Sem permissão, o certo ali é só fechar a janela.
          if (isModal) {
            onCloseModalRef.current?.();
          } else {
            history.push("/tickets");
          }
        }
      }
    };
    fetchTicket();
    return () => {
      if (loadingTimer) clearTimeout(loadingTimer);
      abortController.abort();
    };
    // `ticket` fica fora das deps de propósito: só decide se mostra o loading
    // desta busca, não deve disparar outra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, history, isModal, user?.id, user?.profile, user?.allTicket, userQueueIdsKey]);

  // Mescla um ticket atualizado no estado local preservando as relações que podem
  // não vir no payload. `keepMissingUser` reproduz o comportamento do socket (nunca
  // perde o atendente); nas respostas de PUT o `user` nulo é intencional — é o
  // chamado voltando para a fila.
  const mergeTicketState = useCallback((updated, { keepMissingUser = false } = {}) => {
    if (!updated?.id) return;

    setTicket((prevTicket) => ({
      ...prevTicket,
      ...updated,
      // Garantir que contact não seja perdido
      contact: updated.contact || prevTicket.contact,
      user: keepMissingUser
        ? updated.user || prevTicket.user
        : updated.user !== undefined
        ? updated.user
        : prevTicket.user,
      // Garantir que queue não seja perdida
      queue: updated.queue || prevTicket.queue,
      // Garantir que prompt não seja perdido
      prompt: updated.prompt || prevTicket.prompt,
      // Garantir que whatsapp não seja perdido (merge para preservar campos como `type`)
      whatsapp: updated.whatsapp
        ? {
            ...(prevTicket.whatsapp || {}),
            ...updated.whatsapp,
          }
        : prevTicket.whatsapp,
      // Garantir que tags não sejam perdidas
      tags: updated.tags || prevTicket.tags,
      // Garantir que isGroup não seja perdido (importante para exibir nomes em grupos)
      isGroup: updated.isGroup !== undefined ? updated.isGroup : prevTicket.isGroup,
    }));

    // Atualizar contact se veio no update
    if (updated.contact) {
      setContact((prevContact) => ({
        ...prevContact,
        ...updated.contact,
      }));
    }
  }, []);

  useEffect(() => {
    // Não conecta ao socket se o ticket não foi carregado ainda
    if (!ticketIdForSocket) {
      return;
    }

    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    
    // Capturar o ticketId atual para uso nos handlers (evitar closures stale)
    const currentTicketId = ticketIdForSocket;

    socket.on("ready", () => socket.emit("joinChatBox", `${currentTicketId}`));

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "update" && data.ticket.id === currentTicketId) {
        // Mesclar dados preservando relações essenciais (contact, user, etc)
        mergeTicketState(data.ticket, { keepMissingUser: true });
      }

      // Só fecha o ticket se for delete E não for o ticket atual sendo visualizado
      // (evita fechar quando muda de status pending para open)
      if (data.action === "delete" && data.ticketId === currentTicketId) {
        // Não fecha se o ticket ainda existe - apenas mudou de status
        // O evento "update" virá em seguida para atualizar o status
        return;
      }

      // A conexão deste chamado foi excluída: ele foi fechado e inativado no
      // backend. Admin continua vendo (só leitura); os demais perdem o acesso.
      if (
        data.action === "connectionInactivated" &&
        data.whatsappId &&
        Number(data.whatsappId) === ticketWhatsappIdRef.current
      ) {
        if (isAdminRef.current) {
          mergeTicketState({
            id: currentTicketId,
            status: "closed",
            active: false,
            inactivatedAt: data.inactivatedAt || new Date().toISOString(),
          }, { keepMissingUser: true });
        } else {
          toast.info(i18n.t("tickets.inactiveConnection.removedToast"));
          if (isModal) {
            onCloseModalRef.current?.();
          } else {
            history.push("/tickets");
          }
        }
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    // Status "Agente de IA está digitando..." — dirigido pelo backend, que espelha
    // o indicador real do WhatsApp (liga no ACK do hub, renova em typing/intermediate,
    // para em final/attachment/error). Auto-expira se não for renovado.
    socket.on(`company-${companyId}-aiHubTyping`, (data) => {
      if (!data || data.ticketId !== currentTicketId) return;
      if (aiTypingTimerRef.current) {
        clearTimeout(aiTypingTimerRef.current);
        aiTypingTimerRef.current = null;
      }
      if (data.typing) {
        setAiTypingAgent(data.agentName || "IA");
        aiTypingTimerRef.current = setTimeout(() => setAiTypingAgent(null), 60000);
      } else {
        setAiTypingAgent(null);
      }
    });

    return () => {
      if (aiTypingTimerRef.current) {
        clearTimeout(aiTypingTimerRef.current);
        aiTypingTimerRef.current = null;
      }
      socket.disconnect();
    };
  }, [ticketIdForSocket, socketManager, mergeTicketState, isModal, history]);
  // ⚠️ IMPORTANTE: Usa ticketIdForSocket (primitivo number) em vez de ticket (objeto)
  // para evitar loop infinito: socket update → setTicket → ticket muda → useEffect re-runs
  // (mergeTicketState é estável — useCallback com deps vazias — e não re-dispara o efeito)

  const handleDrawerOpen = () => {
    setSearchDrawerOpen(false);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleSearchDrawerOpen = () => {
    setDrawerOpen(false);
    setSearchDrawerOpen(true);
  };

  const handleSearchDrawerClose = () => {
    setSearchDrawerOpen(false);
  };

  // Ao trocar de ticket, fecha o painel de pesquisa (o componente Ticket
  // é reaproveitado entre tickets — sem key — então o estado persistiria).
  useEffect(() => {
    setSearchDrawerOpen(false);
    setDrawerJump({ id: null, ts: null, ticketId: null });
  }, [ticketId]);

  const handleJumpToMessageFromDrawer = (messageId) => {
    if (!messageId) return;
    setDrawerJump({ id: String(messageId), ts: Date.now(), ticketId: ticket?.id || null });
  };

  const handleCloseConversation = () => {
    history.push("/tickets");
  };

  // ESC fecha apenas a conversa (volta para a lista), igual ao WhatsApp Web —
  // NUNCA encerra/resolve o chamado. Antes disso, fecha o que estiver aberto por
  // cima: painel de pesquisa e ficha do contato. Dialogs/popovers do MUI e o
  // rodapé de digitação (emoji, citação, edição) consomem o ESC antes daqui —
  // o MessageInput marca o evento com preventDefault() quando o consome.
  useEffect(() => {
    if (isModal) return; // dentro do modal o próprio Dialog trata o ESC

    const handleEscape = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (hasOpenOverlay()) return;

      if (searchDrawerOpen) {
        setSearchDrawerOpen(false);
      } else if (drawerOpen) {
        setDrawerOpen(false);
      } else {
        history.push("/tickets");
      }

      e.preventDefault();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isModal, searchDrawerOpen, drawerOpen, history]);

  const hasDraggedFiles = (e) => {
    const types = e?.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes("Files");
  };

  const handleChatDragEnter = (e) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    setDragActive(true);
  };

  const handleChatDragOver = (e) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    setDragActive(true);
  };

  const handleChatDragLeave = (e) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragActive(false);
  };

  const handleChatDrop = (e) => {
    if (!hasDraggedFiles(e)) return;
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;

    window.dispatchEvent(
      new CustomEvent("onspace:conversation-file-drop", {
        detail: { files },
      })
    );
  };

  // Verifica se o ticket pertence a outro usuário (funciona para todos, incluindo admin)
  const isOtherUserTicket = () => {
    if (!ticket || !ticket.id || !ticket.userId) return false;
    const isOther = ticket.userId !== user.id && ticket.status === "open";
    return isOther;
  };

  const isAgentControlledTicket = () => {
    if (!ticket || !ticket.id) return false;
    return (ticket.status === "open" || ticket.status === "pending") && !!ticket.promptId;
  };

  useEffect(() => {
    let cancelled = false;

    const resolveAgentName = async () => {
      const promptId = ticket?.promptId;
      const promptNameFromTicket = ticket?.prompt?.name;

      if (!promptId) {
        if (!cancelled) setAgentName("");
        return;
      }

      if (promptNameFromTicket) {
        if (!cancelled) setAgentName(promptNameFromTicket);
        return;
      }

      try {
        const { data } = await api.get(`/prompt/${promptId}`);
        if (!cancelled) {
          setAgentName(data?.name || i18n.t("contactChat.agent.fallbackName", { id: promptId }));
        }
      } catch (_err) {
        if (!cancelled) {
          setAgentName(i18n.t("contactChat.agent.fallbackName", { id: promptId }));
        }
      }
    };

    resolveAgentName();

    return () => {
      cancelled = true;
    };
  }, [ticket?.promptId, ticket?.prompt?.name]);

  // Função para assumir o chamado
  const handleAssumeTicket = async () => {
    try {
      // Verificar se o ticket tem fila e se o usuário tem acesso a ela
      if (ticket.queueId) {
        const userQueueIds = user.queues.map((q) => q.id);
        if (!userQueueIds.includes(ticket.queueId)) {
          toast.error(i18n.t("tickets.toasts.noQueueAccess"));
          return;
        }
      }
      
      const { data } = await api.put(`/tickets/${ticket.id}`, {
        userId: user.id,
        status: "open",
        transferredBy: user.id, // Indica que o próprio usuário está assumindo
        promptId: null,
      });
      // Atualiza o estado local na hora: sem isso o aviso "pertence a outro
      // atendente" só sai quando o evento de socket chega.
      mergeTicketState(data);
      toast.success(i18n.t("tickets.toasts.assumed"));
    } catch (err) {
      toastError(err);
    }
  };

  const renderTicketInfo = () => {
    // Sempre renderizar TicketInfo quando houver ticket e contact válidos
    if (ticketMatchesRoute && contact && contact.id) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
          whatsappStatus={whatsappStatus}
        />
      );
    }
    return null;
  };

  const renderMessagesList = () => {
    // Não renderiza até que o ticket da rota esteja carregado
    if (!ticketMatchesRoute) {
      return null;
    }

    // Ticket de conexão excluída: só leitura (sem campo de envio nem janela da
    // Cloud API). O backend recusa reabrir/responder de qualquer forma.
    if (isInactiveTicket(ticket)) {
      return (
        <>
          <MessagesList
            key={ticket.id}
            ticket={ticket}
            ticketId={ticket.id}
            isGroup={ticket.isGroup}
            jumpToMessageId={effectiveJumpToMessageId}
            jumpToMessageTs={effectiveJumpToMessageTs}
            messageHistoryModeOverride={messageHistoryModeOverride}
          />
          <Box className={classes.otherUserWarning}>
            <Typography className={classes.warningText} style={{ marginBottom: 0 }}>
              <LinkOffIcon fontSize="small" />
              {i18n.t("tickets.inactiveConnection.readOnly")}
            </Typography>
          </Box>
        </>
      );
    }

    const blockedByOtherUser = isOtherUserTicket();
    const blockedByAgent = isAgentControlledTicket();

    // Se o ticket está sendo atendido por outro usuário ou por agente, mostra aviso
    if (blockedByOtherUser || blockedByAgent) {
      const otherUserName = ticket.user?.name || i18n.t("contactChat.otherUsers.anotherAttendant");
      const resolvedAgentName = agentName || ticket.prompt?.name || i18n.t("contactChat.agent.fallbackName", { id: ticket.promptId });
      // Quando o agente está digitando (callback typing/intermediate do hub, ou ACK),
      // troca o texto do rodapé por "está digitando..." no mesmo lugar.
      const agentTypingNow = blockedByAgent && aiTypingAgent;
      const warningText = agentTypingNow
        ? i18n.t("contactChat.agent.typing", { name: resolvedAgentName })
        : blockedByAgent
        ? i18n.t("contactChat.agent.handling", { name: resolvedAgentName })
        : i18n.t("contactChat.otherUsers.belongsTo", { name: otherUserName });

      return (
        <>
          <MessagesList
            key={ticket.id}
            ticket={ticket}
            ticketId={ticket.id}
            isGroup={ticket.isGroup}
            jumpToMessageId={effectiveJumpToMessageId}
            jumpToMessageTs={effectiveJumpToMessageTs}
            messageHistoryModeOverride={messageHistoryModeOverride}
          />
          <Box className={classes.otherUserWarning}>
            <Typography
              className={classes.warningText}
              style={agentTypingNow ? { fontStyle: "italic", color: isDark ? teal[300] : "#075e54" } : undefined}
            >
              {agentTypingNow ? <span style={{ fontSize: 16 }}>🤖</span> : <PersonIcon fontSize="small" />}
              {warningText}
            </Typography>
            <Button
              variant="contained"
              className={classes.assumeButton}
              onClick={handleAssumeTicket}
              startIcon={<PersonIcon />}
            >
              {i18n.t("contactChat.otherUsers.assume")}
            </Button>
          </Box>
        </>
      );
    }
    
    return (
      <>
        <MessagesList
          key={ticket.id} // Força re-render quando muda de ticket
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
          jumpToMessageId={effectiveJumpToMessageId}
          jumpToMessageTs={effectiveJumpToMessageTs}
          messageHistoryModeOverride={messageHistoryModeOverride}
        ></MessagesList>
        {ticket.whatsapp?.type === "cloudapi" && (
          <CloudApiWindowBanner
            ticketId={ticket.id}
            whatsapp={ticket.whatsapp}
            onWindowStatusChange={setCloudApiWindowOpen}
          />
        )}
        <MessageInput ticketId={ticket.id} ticketUuid={ticket.uuid} ticketStatus={ticket.status} ticketUserId={ticket.userId} assumeOnSend onTicketUpdated={mergeTicketState} isGroup={ticket.isGroup} contactId={ticket.contactId || contact?.id} isModal={isModal} whatsapp={ticket.whatsapp} cloudApiWindowOpen={cloudApiWindowOpen} ticketChannel={ticket.channel} />
      </>
    );
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen || searchDrawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          {!isModal && (
            <IconButton
              className={classes.backButton}
              onClick={handleCloseConversation}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {isModal && (
            <IconButton
              className={classes.backButton}
              onClick={onCloseModal}
              aria-label={i18n.t("tickets.closePreview")}
            >
              {/* Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: posicionar
                  o fechamento no fluxo do cabeçalho para não colidir com o botão REABRIR. */}
              <CloseIcon />
            </IconButton>
          )}
          {renderTicketInfo()}
          <TicketActionButtons
            ticket={ticketMatchesRoute ? ticket : {}}
            onSearchClick={handleSearchDrawerOpen}
            onTicketUpdated={mergeTicketState}
            isModal={isModal}
            onCloseModal={onCloseModal}
            assumeOnCall
          />
        </TicketHeader>
        <div
          className={classes.chatDropZone}
          onDragEnter={handleChatDragEnter}
          onDragOver={handleChatDragOver}
          onDragLeave={handleChatDragLeave}
          onDrop={handleChatDrop}
        >
          {dragActive && (
            <div className={classes.chatDropOverlay}>{i18n.t("tickets.dropFileHere")}</div>
          )}
          <Paper>
            {ticketMatchesRoute && <TagsContainer ticket={ticket} />}
            {ticketMatchesRoute && !isInactiveTicket(ticket) && (
              <ConnectionDisconnectedBanner
                whatsappId={ticket.whatsappId}
                whatsapp={ticket.whatsapp}
                ticketStatus={ticket.status}
              />
            )}
          </Paper>
          <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
        </div>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
        onJumpToMessage={handleJumpToMessageFromDrawer}
        whatsappStatus={whatsappStatus}
      />
      <MessageSearchDrawer
        open={searchDrawerOpen}
        handleDrawerClose={handleSearchDrawerClose}
        ticket={ticket}
        onJumpToMessage={handleJumpToMessageFromDrawer}
      />
    </div>
  );
};

export default Ticket;
